import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import TrendInsightsFooter from './TrendInsightsFooter';
import { PEER_COLORS, BAR_MAX_SIZE, gridProps, xAxisProps, CHART_INK, CHART_TYPE } from './chartTheme';
import { isEstimatedValue, ESTIMATED_FILL_OPACITY } from './chartNotes';
import { formatFiscalYear, formatCurrencyValue } from '../utils/formatters';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
}

export default function InvestedCapitalTrendChart({ data, region }: Props) {
  const chartData = data.map((d) => ({
    year: formatFiscalYear(d.year, region),
    investedCapital: d.invested_capital,
    estimated: isEstimatedValue(d.data_notes, 'invested_capital'),
  }));

  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold text-neutral-800">
          Invested Capital Trend ({region?.currency?.unit_abbr})
        </h3>
        <p className="text-xs text-neutral-500 mt-0.5">Historical invested capital with CAGR analysis</p>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 20, right: 16, left: 8, bottom: 0 }}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="year" {...xAxisProps} />
            <YAxis
              width={80}
              tick={{ fontSize: CHART_TYPE.axisTick, fill: CHART_INK.label }}
              tickFormatter={(v) => formatCurrencyValue(v, region, true)}
            />
            <Tooltip
              formatter={(value: number) => [
                formatCurrencyValue(value, region),
                'Invested Capital',
              ]}
            />
            <Bar dataKey="investedCapital" name="Invested Capital" fill={PEER_COLORS[0]} radius={[3, 3, 0, 0]} maxBarSize={BAR_MAX_SIZE}>
              {chartData.map((d, i) => (
                <Cell key={i} fillOpacity={d.estimated ? ESTIMATED_FILL_OPACITY : 1} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <TrendInsightsFooter
          values={data.map((d) => d.invested_capital ?? null)}
          metricLabel="Invested Capital"
        />
      </CardBody>
    </Card>
  );
}
